/**
 * Business-day calendar used by fixed-income pricing conventions.
 *
 * Port of crawler/ticker_data_crawler/services/treasury_business_calendar.py:
 * the same holidays and counting rules keep simulated prices consistent with
 * the risk metrics materialized by the crawler.
 */

const MS_PER_DAY = 86_400_000;

const toUtcDate = (year: number, month: number, day: number) => new Date(Date.UTC(year, month - 1, day));

export const parseIsoDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return toUtcDate(year, month, day);
};

export const formatIsoDate = (value: Date): string => value.toISOString().slice(0, 10);

export const addDays = (value: Date, days: number): Date => new Date(value.getTime() + days * MS_PER_DAY);

export const addMonths = (value: Date, months: number): Date => {
  const year = value.getUTCFullYear();
  const month = value.getUTCMonth() + months;
  const targetYear = year + Math.floor(month / 12);
  const targetMonth = ((month % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  return toUtcDate(targetYear, targetMonth + 1, Math.min(value.getUTCDate(), lastDay));
};

export const calendarDaysBetween = (start: Date, end: Date): number =>
  Math.round((end.getTime() - start.getTime()) / MS_PER_DAY);

const easterSunday = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const correction = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * correction) / 451);
  const month = Math.floor((h + correction - 7 * m + 114) / 31);
  const day = ((h + correction - 7 * m + 114) % 31) + 1;
  return toUtcDate(year, month, day);
};

/** Fixed and movable holidays of the Brazilian market calendar, as ISO dates. */
export const brazilianMarketHolidays = (year: number): Set<string> => {
  const easter = easterSunday(year);
  const fixed = [
    [1, 1],
    [4, 21],
    [5, 1],
    [9, 7],
    [10, 12],
    [11, 2],
    [11, 15],
    [11, 20],
    [12, 25],
  ] as const;
  const movableOffsets = [-48, -47, -2, 60];
  const holidays = new Set<string>();
  for (const [month, day] of fixed) holidays.add(formatIsoDate(toUtcDate(year, month, day)));
  for (const offset of movableOffsets) holidays.add(formatIsoDate(addDays(easter, offset)));
  return holidays;
};

/**
 * Count Brazilian market business days from start (inclusive) to end
 * (exclusive), mirroring the Python crawler implementation.
 *
 * Uses a closed-form weekday count minus holidays with a per-date memo, so
 * long-dated simulations (e.g. RendA+ maturing in 2084) do not walk the
 * calendar day by day on every discount-factor evaluation.
 */
const BD_ORIGIN = Date.UTC(2000, 0, 1);
const BD_ORIGIN_WEEKDAY = 6; // 2000-01-01 was a Saturday.
const bdIndexCache = new Map<string, number>();

const businessDayIndexFromOrigin = (date: Date): number => {
  const iso = formatIsoDate(date);
  const cached = bdIndexCache.get(iso);
  if (cached !== undefined) return cached;

  const days = Math.round((date.getTime() - BD_ORIGIN) / MS_PER_DAY);
  const fullWeeks = Math.floor(days / 7);
  const remainder = days % 7;
  let count = fullWeeks * 5;
  for (let offset = 0; offset < remainder; offset += 1) {
    const weekday = (BD_ORIGIN_WEEKDAY + offset) % 7;
    if (weekday >= 1 && weekday <= 5) count += 1;
  }
  const endYear = date.getUTCFullYear();
  for (let year = 2000; year <= endYear; year += 1) {
    brazilianMarketHolidays(year).forEach((holiday) => {
      const holidayDate = parseIsoDate(holiday);
      if (holidayDate.getTime() >= date.getTime()) return;
      const weekday = holidayDate.getUTCDay();
      if (weekday >= 1 && weekday <= 5) count -= 1;
    });
  }
  bdIndexCache.set(iso, count);
  return count;
};

const businessDaysBetweenSlow = (start: Date, end: Date): number => {
  const holidays = new Set<string>();
  for (let year = start.getUTCFullYear(); year <= end.getUTCFullYear(); year += 1) {
    brazilianMarketHolidays(year).forEach((holiday) => holidays.add(holiday));
  }
  let current = start;
  let days = 0;
  while (current.getTime() < end.getTime()) {
    const weekday = current.getUTCDay();
    if (weekday >= 1 && weekday <= 5 && !holidays.has(formatIsoDate(current))) days += 1;
    current = addDays(current, 1);
  }
  return days;
};

export const businessDaysBetween = (start: Date, end: Date): number => {
  if (end.getTime() < start.getTime()) throw new Error('end date must not be before start date');
  if (start.getTime() < BD_ORIGIN) return businessDaysBetweenSlow(start, end);
  return businessDayIndexFromOrigin(end) - businessDayIndexFromOrigin(start);
};
